import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Search, Check, X, Eye, RefreshCcw, ShieldCheck, ShieldX, Clock,
  Plus, Trash2, UserCheck, FileText, Upload, Pencil, Download,
} from "lucide-react";
import { toast } from "sonner";
import AdminLayout from "../components/AdminLayout";
import LoadingButton from "../components/LoadingButton";
import { api, formatApiError, formatDate } from "../lib/api";

const STATUS_PRESETS = [
  { key: "", label: "Semua" },
  { key: "pending", label: "Menunggu" },
  { key: "active", label: "Aktif" },
  { key: "rejected", label: "Ditolak" },
];

const EMPTY_FORM = {
  full_name: "", email: "", password: "", ktp_no: "", phone: "",
  account_no: "", bank_account_number: "", address: "", place_of_birth: "",
  date_of_birth: "", gender: "", occupation: "", position: "", work_unit: "",
  membership_type: "biasa", borrower_type_id: "", activate: false,
  partner: { name: "", phone: "", bank_account_number: "" },
  heritages: [],
};

export default function AdminMembers() {
  const [params, setParams] = useSearchParams();
  const [members, setMembers] = useState([]);
  const [borrowerTypes, setBorrowerTypes] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState(params.get("status") || "");
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [rejectMode, setRejectMode] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [busy, setBusy] = useState("");
  const [checked, setChecked] = useState([]);

  // Create / edit form
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/admin/members", { params: status ? { status } : {} });
      setMembers(data);
      setChecked([]);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    api.get("/admin/master-data").then((r) => setBorrowerTypes(r.data.borrower_types || [])).catch(() => {});
  }, []);

  useEffect(() => {
    const id = params.get("id");
    if (id) loadDetail(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  const loadDetail = async (id) => {
    try {
      const { data } = await api.get(`/admin/members/${id}`);
      setSelected(id);
      setDetail(data);
      setRejectMode(false);
      setRejectReason("");
    } catch (e) { setError(formatApiError(e)); }
  };

  const approve = async () => {
    setBusy("approve");
    try {
      await api.post(`/admin/members/${selected}/approve`, {});
      toast.success("Anggota berhasil disetujui");
      await load();
      await loadDetail(selected);
    } catch (e) { setError(formatApiError(e)); }
    finally { setBusy(""); }
  };

  const reject = async () => {
    if (!rejectReason.trim()) return setError("Alasan penolakan wajib diisi");
    setBusy("reject");
    try {
      await api.post(`/admin/members/${selected}/reject`, { reason: rejectReason });
      toast.success("Pendaftaran ditolak");
      await load();
      await loadDetail(selected);
      setRejectMode(false);
    } catch (e) { setError(formatApiError(e)); }
    finally { setBusy(""); }
  };

  const toggleCheck = (id) => {
    setChecked((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };
  const toggleAll = () => {
    setChecked(checked.length === filtered.length ? [] : filtered.map((m) => m.id));
  };

  const bulkActivate = async () => {
    if (!checked.length) return;
    setBusy("bulk");
    try {
      const { data } = await api.post("/admin/members/bulk-activate", { member_ids: checked });
      toast.success(data.message || "Anggota diaktifkan");
      await load();
    } catch (e) { setError(formatApiError(e)); }
    finally { setBusy(""); }
  };

  const activateAll = async () => {
    if (!window.confirm("Aktifkan SEMUA anggota yang belum aktif?")) return;
    setBusy("all");
    try {
      const { data } = await api.post("/admin/members/activate-all", {});
      toast.success(data.message || "Semua anggota diaktifkan");
      await load();
    } catch (e) { setError(formatApiError(e)); }
    finally { setBusy(""); }
  };

  const bulkDelete = async () => {
    if (!checked.length) return;
    if (!window.confirm(`Hapus ${checked.length} anggota terpilih? Tindakan ini permanen.`)) return;
    setBusy("del");
    try {
      const { data } = await api.delete("/admin/members/bulk-delete", { data: { member_ids: checked } });
      toast.success(data.message || "Anggota dihapus");
      setChecked([]);
      await load();
    } catch (e) { setError(formatApiError(e)); }
    finally { setBusy(""); }
  };

  const openCreate = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setShowForm(true);
  };

  const openEdit = (d) => {
    setEditingId(d.id);
    setForm({
      full_name: d.full_name || "", email: d.email || "", password: "",
      ktp_no: d.ktp_no || "", phone: d.phone || "", account_no: d.account_no || "",
      bank_account_number: d.bank_account_number || "", address: d.address || "",
      place_of_birth: d.place_of_birth || "", date_of_birth: d.date_of_birth || "",
      gender: d.gender || "", occupation: d.occupation || "", position: d.position || "",
      work_unit: d.work_unit || "", membership_type: d.membership_type || "biasa",
      borrower_type_id: d.borrower_type_id || "", activate: false,
      partner: d.partner ? { name: d.partner.name || "", phone: d.partner.phone || "", bank_account_number: d.partner.bank_account_number || "" } : EMPTY_FORM.partner,
      heritages: (d.heritages || []).map((h) => ({ name: h.name || "", phone: h.phone || "", bank_account_number: h.bank_account_number || "" })),
    });
    setShowForm(true);
  };

  const submitForm = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      const payload = { ...form };
      if (!payload.password) delete payload.password;
      if (payload.borrower_type_id === "") payload.borrower_type_id = null;
      if (!payload.partner?.name) delete payload.partner;
      payload.heritages = (payload.heritages || []).filter((h) => h.name);
      if (editingId) {
        await api.put(`/admin/members/${editingId}`, payload);
        toast.success("Data anggota diperbarui");
      } else {
        await api.post("/admin/members", payload);
        toast.success("Anggota baru dibuat");
      }
      setShowForm(false);
      await load();
      if (editingId && selected === editingId) await loadDetail(editingId);
    } catch (e) { setError(formatApiError(e)); }
    finally { setSubmitting(false); }
  };

  const uploadPdf = async (memberId, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("member_id", memberId);
    fd.append("name", file.name);
    fd.append("file", file);
    try {
      await api.post("/admin/form-pdf", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("PDF formulir berhasil diunggah");
      await loadDetail(memberId);
    } catch (e) { setError(formatApiError(e)); }
  };

  const downloadPdf = async (memberId) => {
    try {
      const res = await api.get(`/admin/members/${memberId}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      window.open(url, "_blank");
    } catch (e) { setError(formatApiError(e)); }
  };

  const filtered = members.filter((m) =>
    !q || [m.full_name, m.email, m.ktp_no, m.phone, m.member_number, m.work_unit, m.position].filter(Boolean).join(" ").toLowerCase().includes(q.toLowerCase())
  );

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <h1 className="font-heading font-extrabold text-3xl sm:text-4xl tracking-tight text-brand-ink">Anggota</h1>
            <p className="text-brand-muted text-sm mt-1">Kelola pendaftaran dan data anggota koperasi.</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button onClick={load} className="rounded-full border border-brand/20 px-4 py-2 text-sm flex items-center gap-2 text-brand" data-testid="refresh-members">
              <RefreshCcw size={14} /> Muat Ulang
            </button>
            <button onClick={openCreate} data-testid="open-member-form-btn" className="rounded-full bg-brand text-white px-5 py-2.5 text-sm flex items-center gap-2 hover:bg-brand-hover transition">
              <Plus size={14} /> Tambah Anggota
            </button>
          </div>
        </div>

        <div className="bg-white rounded-3xl border border-brand/10 shadow-sm p-4 sm:p-5 space-y-3">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="relative flex-1 min-w-[240px]">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-muted" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Cari nama, email, KTP, nomor anggota, unit kerja…"
                className="w-full rounded-2xl border border-brand/20 pl-9 pr-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand/30"
                data-testid="members-search-input"
              />
            </div>
            <div className="flex gap-1">
              {STATUS_PRESETS.map((s) => (
                <button
                  key={s.key || "all"}
                  onClick={() => { setStatus(s.key); setParams(s.key ? { status: s.key } : {}); }}
                  data-testid={`filter-status-${s.key || "all"}`}
                  className={"rounded-full px-3 py-1.5 text-xs " + (status === s.key ? "bg-brand text-white" : "bg-brand-soft text-brand hover:bg-brand-soft/70")}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>

          {checked.length > 0 && (
            <div className="flex flex-wrap items-center gap-2 rounded-2xl bg-brand-soft/60 px-3 py-2 text-sm">
              <span className="text-brand font-medium">{checked.length} dipilih</span>
              <LoadingButton onClick={bulkActivate} loading={busy === "bulk"} loadingText="Memproses…" className="rounded-full bg-emerald-500 hover:bg-emerald-600 text-white px-4 py-1.5 text-xs flex items-center gap-1">
                <UserCheck size={13} /> Aktifkan Terpilih
              </LoadingButton>
              <LoadingButton onClick={bulkDelete} loading={busy === "del"} loadingText="Menghapus…" className="rounded-full bg-red-500 hover:bg-red-600 text-white px-4 py-1.5 text-xs flex items-center gap-1">
                <Trash2 size={13} /> Hapus Terpilih
              </LoadingButton>
              <button onClick={() => setChecked([])} className="rounded-full border border-brand/20 px-4 py-1.5 text-xs">Batal</button>
            </div>
          )}

          <div className="flex justify-end">
            <button onClick={activateAll} disabled={busy === "all"} data-testid="activate-all-btn" className="text-xs rounded-full border border-emerald-300 text-emerald-700 px-4 py-1.5 hover:bg-emerald-50 disabled:opacity-60">
              Aktifkan Semua Anggota
            </button>
          </div>
        </div>

        {error && <div className="rounded-2xl bg-red-50 border border-red-200 text-red-600 text-sm px-4 py-3">{error}</div>}

        <div className="bg-white rounded-3xl border border-brand/10 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="members-table">
              <thead className="bg-brand-soft/60">
                <tr className="text-left text-brand-muted">
                  <th className="px-3 py-3">
                    <input type="checkbox" checked={filtered.length > 0 && checked.length === filtered.length} onChange={toggleAll} aria-label="select all" />
                  </th>
                  <th className="px-4 py-3 font-semibold">No Anggota</th>
                  <th className="px-4 py-3 font-semibold">Nama</th>
                  <th className="px-4 py-3 font-semibold">Unit Kerja</th>
                  <th className="px-4 py-3 font-semibold">Kontak</th>
                  <th className="px-4 py-3 font-semibold">Status</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={7} className="px-4 py-10 text-center text-brand-muted">Memuat…</td></tr>
                ) : filtered.length === 0 ? (
                  <tr><td colSpan={7} className="px-4 py-10 text-center text-brand-muted">Tidak ada anggota</td></tr>
                ) : filtered.map((m) => (
                  <tr key={m.id} className="border-t border-brand/10 hover:bg-brand-soft/30">
                    <td className="px-3 py-3">
                      <input type="checkbox" checked={checked.includes(m.id)} onChange={() => toggleCheck(m.id)} aria-label={`select-${m.id}`} />
                    </td>
                    <td className="px-4 py-3 font-medium text-brand-ink">{m.member_number || <span className="text-brand-muted">-</span>}</td>
                    <td className="px-4 py-3">
                      <div className="font-medium text-brand-ink">{m.full_name}</div>
                      <div className="text-xs text-brand-muted">{m.position || "-"}</div>
                    </td>
                    <td className="px-4 py-3 text-brand-muted">{m.work_unit || "-"}</td>
                    <td className="px-4 py-3 text-brand-muted">
                      <div>{m.email}</div>
                      <div className="text-xs">{m.phone}</div>
                    </td>
                    <td className="px-4 py-3"><StatusBadge status={m.status} /></td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => loadDetail(m.id)}
                        className="rounded-full bg-brand text-white text-xs px-3 py-1.5 inline-flex items-center gap-1 hover:bg-brand-hover"
                        data-testid={`view-member-${m.id}`}
                      >
                        <Eye size={12} /> Detail
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {selected && detail && (
        <div className="fixed inset-0 z-50 bg-brand-ink/50 backdrop-blur-sm flex items-end sm:items-center justify-center p-3" onClick={() => { setSelected(null); setDetail(null); setParams({}); }}>
          <div className="bg-white rounded-3xl w-full sm:max-w-3xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="member-detail-modal">
            <div className="sticky top-0 bg-white/95 backdrop-blur px-5 py-4 border-b border-brand/10 flex items-center justify-between z-10">
              <div>
                <div className="font-heading font-bold text-brand-ink text-lg">{detail.full_name}</div>
                <div className="text-xs text-brand-muted">{detail.member_number || "Belum bernomor"} · <StatusBadge status={detail.status} inline /></div>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => openEdit(detail)} className="rounded-full border border-brand/20 px-3 py-1.5 text-xs flex items-center gap-1 text-brand"><Pencil size={12} /> Edit</button>
                <button onClick={() => { setSelected(null); setDetail(null); setParams({}); }} className="p-2 rounded-full hover:bg-brand-soft" aria-label="close"><X size={16} /></button>
              </div>
            </div>
            <div className="p-5 space-y-5">
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
                <Info label="Email" value={detail.email} />
                <Info label="No HP" value={detail.phone} />
                <Info label="No KTP" value={detail.ktp_no} />
                <Info label="No Anggota" value={detail.account_no || "-"} />
                <Info label="No Rekening" value={detail.bank_account_number || "-"} />
                <Info label="Jabatan/Posisi" value={detail.position || "-"} />
                <Info label="Unit Kerja" value={detail.work_unit || "-"} />
                <Info label="Jenis Peminjam" value={detail.borrower_type_name || "-"} />
                <Info label="Jenis Anggota" value={detail.membership_type} />
                <Info label="Tgl Daftar" value={formatDate(detail.registered_at)} />
                <Info label="Tempat Lahir" value={detail.place_of_birth || "-"} />
                <Info label="Tgl Lahir" value={formatDate(detail.date_of_birth)} />
                <Info label="Alamat" value={detail.address || "-"} className="sm:col-span-3" />
                {detail.rejection_reason && <Info label="Alasan Penolakan" value={detail.rejection_reason} className="sm:col-span-3" />}
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {detail.photo_selfie && <ImgBox label="Foto Diri" src={detail.photo_selfie} />}
                {detail.photo_ktp && <ImgBox label="Foto KTP" src={detail.photo_ktp} />}
                {detail.identity_photo && <ImgBox label="Foto Identitas" src={detail.identity_photo} />}
                {detail.signature && <ImgBox label="Tanda Tangan" src={detail.signature} contain />}
              </div>

              <section className="rounded-2xl border border-brand/10 p-4">
                <div className="text-xs font-semibold uppercase tracking-[0.18em] text-brand-muted mb-2">Data Pasangan</div>
                {detail.partner ? (
                  <div className="grid grid-cols-3 gap-3 text-sm">
                    <Info label="Nama" value={detail.partner.name} />
                    <Info label="No HP" value={detail.partner.phone} />
                    <Info label="No Rekening" value={detail.partner.bank_account_number} />
                  </div>
                ) : <div className="text-sm text-brand-muted">Belum ada data pasangan.</div>}
              </section>

              <section className="rounded-2xl border border-brand/10 p-4">
                <div className="text-xs font-semibold uppercase tracking-[0.18em] text-brand-muted mb-2">Ahli Waris</div>
                {(detail.heritages || []).length === 0 ? (
                  <div className="text-sm text-brand-muted">Belum ada data ahli waris.</div>
                ) : (
                  <div className="space-y-2">
                    {detail.heritages.map((h) => (
                      <div key={h.id} className="grid grid-cols-3 gap-3 text-sm">
                        <Info label="Nama" value={h.name} />
                        <Info label="No HP" value={h.phone} />
                        <Info label="No Rekening" value={h.bank_account_number} />
                      </div>
                    ))}
                  </div>
                )}
              </section>

              <section className="rounded-2xl border border-brand/10 p-4">
                <div className="text-xs font-semibold uppercase tracking-[0.18em] text-brand-muted mb-2">Formulir PDF</div>
                <div className="flex flex-wrap items-center gap-3">
                  {detail.has_form_pdf && (
                    <button onClick={() => downloadPdf(detail.id)} className="rounded-full bg-brand-soft text-brand px-4 py-2 text-xs flex items-center gap-1"><Download size={13} /> Lihat PDF</button>
                  )}
                  <label className="rounded-full border border-brand/20 px-4 py-2 text-xs flex items-center gap-1 cursor-pointer text-brand">
                    <Upload size={13} /> Unggah PDF
                    <input type="file" accept="application/pdf" className="hidden" onChange={(e) => uploadPdf(detail.id, e.target.files?.[0])} />
                  </label>
                  {!detail.has_form_pdf && <span className="text-xs text-brand-muted flex items-center gap-1"><FileText size={13} /> Belum ada PDF</span>}
                </div>
              </section>

              {detail.status === "pending" && (
                <div className="rounded-2xl border border-brand/10 p-4 bg-brand-soft/40">
                  {!rejectMode ? (
                    <div className="flex flex-wrap items-center gap-2">
                      <LoadingButton onClick={approve} loading={busy === "approve"} loadingText="Menyetujui…" data-testid="approve-member-btn" className="rounded-full bg-emerald-500 hover:bg-emerald-600 text-white px-5 py-2.5 text-sm"><ShieldCheck size={14} /> Setujui</LoadingButton>
                      <button onClick={() => setRejectMode(true)} disabled={busy === "approve"} data-testid="reject-mode-btn" className="rounded-full bg-red-500 hover:bg-red-600 text-white px-5 py-2.5 text-sm flex items-center gap-2 disabled:opacity-60"><ShieldX size={14} /> Tolak</button>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <div className="text-sm font-semibold">Alasan Penolakan</div>
                      <textarea data-testid="reject-reason-input" value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} className="w-full rounded-xl border border-brand/20 p-3 text-sm h-20" />
                      <div className="flex gap-2">
                        <LoadingButton onClick={reject} loading={busy === "reject"} loadingText="Memproses…" data-testid="confirm-reject-btn" className="rounded-full bg-red-500 hover:bg-red-600 text-white px-4 py-2 text-sm">Konfirmasi Tolak</LoadingButton>
                        <button onClick={() => setRejectMode(false)} disabled={busy === "reject"} className="rounded-full border border-brand/20 px-4 py-2 text-sm disabled:opacity-60">Batal</button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {showForm && (
        <div className="fixed inset-0 z-50 bg-brand-ink/50 backdrop-blur-sm flex items-end sm:items-center justify-center p-3" onClick={() => setShowForm(false)}>
          <form onSubmit={submitForm} className="bg-white rounded-3xl w-full sm:max-w-3xl max-h-[92vh] overflow-y-auto p-5 sm:p-6 space-y-4" onClick={(e) => e.stopPropagation()} data-testid="member-form">
            <div className="font-heading font-bold text-lg text-brand-ink">{editingId ? "Edit Anggota" : "Tambah Anggota"}</div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Nama Lengkap"><input required value={form.full_name} onChange={(e) => setForm((p) => ({ ...p, full_name: e.target.value }))} className="inp" /></Field>
              <Field label="Email"><input required type="email" value={form.email} onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))} className="inp" /></Field>
              <Field label="No KTP"><input required value={form.ktp_no} onChange={(e) => setForm((p) => ({ ...p, ktp_no: e.target.value }))} className="inp" /></Field>
              <Field label="No HP"><input required value={form.phone} onChange={(e) => setForm((p) => ({ ...p, phone: e.target.value }))} className="inp" /></Field>
              <Field label="No Anggota"><input value={form.account_no} onChange={(e) => setForm((p) => ({ ...p, account_no: e.target.value }))} className="inp" /></Field>
              <Field label="No Rekening Bank"><input value={form.bank_account_number} onChange={(e) => setForm((p) => ({ ...p, bank_account_number: e.target.value }))} className="inp" /></Field>
              <Field label="Jabatan / Posisi"><input value={form.position} onChange={(e) => setForm((p) => ({ ...p, position: e.target.value }))} className="inp" /></Field>
              <Field label="Unit Kerja"><input value={form.work_unit} onChange={(e) => setForm((p) => ({ ...p, work_unit: e.target.value }))} className="inp" /></Field>
              <Field label="Jenis Peminjam">
                <select value={form.borrower_type_id} onChange={(e) => setForm((p) => ({ ...p, borrower_type_id: e.target.value }))} className="inp">
                  <option value="">— Pilih —</option>
                  {borrowerTypes.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
              </Field>
              <Field label="Jenis Anggota">
                <select value={form.membership_type} onChange={(e) => setForm((p) => ({ ...p, membership_type: e.target.value }))} className="inp">
                  <option value="biasa">Biasa</option>
                  <option value="luarbiasa">Luar Biasa</option>
                  <option value="kehormatan">Kehormatan</option>
                </select>
              </Field>
              <Field label="Tempat Lahir"><input value={form.place_of_birth} onChange={(e) => setForm((p) => ({ ...p, place_of_birth: e.target.value }))} className="inp" /></Field>
              <Field label="Tanggal Lahir"><input type="date" value={form.date_of_birth} onChange={(e) => setForm((p) => ({ ...p, date_of_birth: e.target.value }))} className="inp" /></Field>
              <Field label="Alamat" className="sm:col-span-2"><input value={form.address} onChange={(e) => setForm((p) => ({ ...p, address: e.target.value }))} className="inp" /></Field>
              {!editingId && <Field label="Password (opsional, default No KTP)"><input type="text" value={form.password} onChange={(e) => setForm((p) => ({ ...p, password: e.target.value }))} className="inp" /></Field>}
            </div>

            <div className="rounded-2xl border border-brand/10 p-4 space-y-3">
              <div className="text-xs font-semibold uppercase tracking-[0.18em] text-brand-muted">Pasangan</div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <input placeholder="Nama" value={form.partner.name} onChange={(e) => setForm((p) => ({ ...p, partner: { ...p.partner, name: e.target.value } }))} className="inp" />
                <input placeholder="No HP" value={form.partner.phone} onChange={(e) => setForm((p) => ({ ...p, partner: { ...p.partner, phone: e.target.value } }))} className="inp" />
                <input placeholder="No Rekening" value={form.partner.bank_account_number} onChange={(e) => setForm((p) => ({ ...p, partner: { ...p.partner, bank_account_number: e.target.value } }))} className="inp" />
              </div>
            </div>

            <div className="rounded-2xl border border-brand/10 p-4 space-y-3">
              <div className="flex items-center justify-between">
                <div className="text-xs font-semibold uppercase tracking-[0.18em] text-brand-muted">Ahli Waris</div>
                <button type="button" onClick={() => setForm((p) => ({ ...p, heritages: [...p.heritages, { name: "", phone: "", bank_account_number: "" }] }))} className="text-xs rounded-full bg-brand-soft text-brand px-3 py-1">+ Tambah</button>
              </div>
              {form.heritages.length === 0 && <div className="text-xs text-brand-muted">Belum ada ahli waris.</div>}
              {form.heritages.map((h, i) => (
                <div key={i} className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_1fr_auto] gap-2">
                  <input placeholder="Nama" value={h.name} onChange={(e) => setForm((p) => { const a = [...p.heritages]; a[i] = { ...a[i], name: e.target.value }; return { ...p, heritages: a }; })} className="inp" />
                  <input placeholder="No HP" value={h.phone} onChange={(e) => setForm((p) => { const a = [...p.heritages]; a[i] = { ...a[i], phone: e.target.value }; return { ...p, heritages: a }; })} className="inp" />
                  <input placeholder="No Rekening" value={h.bank_account_number} onChange={(e) => setForm((p) => { const a = [...p.heritages]; a[i] = { ...a[i], bank_account_number: e.target.value }; return { ...p, heritages: a }; })} className="inp" />
                  <button type="button" onClick={() => setForm((p) => ({ ...p, heritages: p.heritages.filter((_, x) => x !== i) }))} className="rounded-xl border border-red-200 text-red-500 px-3"><Trash2 size={14} /></button>
                </div>
              ))}
            </div>

            {!editingId && (
              <label className="flex items-center gap-2 text-sm text-brand-ink">
                <input type="checkbox" checked={form.activate} onChange={(e) => setForm((p) => ({ ...p, activate: e.target.checked }))} />
                Langsung aktifkan & buat nomor anggota + simpanan pokok/wajib
              </label>
            )}

            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={() => setShowForm(false)} disabled={submitting} className="rounded-full border border-brand/20 px-4 py-2 text-sm disabled:opacity-60">Batal</button>
              <LoadingButton type="submit" loading={submitting} loadingText="Menyimpan…" className="rounded-full bg-brand text-white px-5 py-2 text-sm hover:bg-brand-hover">Simpan</LoadingButton>
            </div>
            <style>{`.inp{width:100%;border-radius:12px;border:1px solid rgba(11,110,110,0.2);padding:9px 12px;font-size:14px;background:white;outline:none}.inp:focus{box-shadow:0 0 0 3px rgba(11,110,110,0.15);border-color:rgba(11,110,110,0.4)}`}</style>
          </form>
        </div>
      )}
    </AdminLayout>
  );
}

function StatusBadge({ status, inline }) {
  const map = {
    pending: { c: "bg-amber-100 text-amber-700", icon: Clock, label: "Menunggu" },
    active: { c: "bg-emerald-100 text-emerald-700", icon: Check, label: "Aktif" },
    rejected: { c: "bg-red-100 text-red-700", icon: X, label: "Ditolak" },
  }[status] || { c: "bg-slate-100 text-slate-600", icon: Clock, label: status };
  const Icon = map.icon;
  return (
    <span className={"inline-flex items-center gap-1 rounded-full text-xs px-2.5 py-1 " + map.c + (inline ? " ml-1" : "")}>
      <Icon size={12} /> {map.label}
    </span>
  );
}

function Info({ label, value, className = "" }) {
  return (
    <div className={"rounded-xl bg-brand-soft/50 px-3 py-2 " + className}>
      <div className="text-[10px] uppercase tracking-[0.18em] text-brand-muted">{label}</div>
      <div className="text-sm font-medium text-brand-ink break-words">{value || "-"}</div>
    </div>
  );
}

function Field({ label, children, className = "" }) {
  return (
    <label className={"block " + className}>
      <div className="text-xs font-semibold text-brand-ink mb-1.5 uppercase tracking-[0.14em]">{label}</div>
      {children}
    </label>
  );
}

function ImgBox({ label, src, contain }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.18em] text-brand-muted mb-1">{label}</div>
      <img src={src} alt={label} className={"rounded-xl border border-brand/10 w-full aspect-square " + (contain ? "object-contain bg-white" : "object-cover")} />
    </div>
  );
}
